import * as Cookie from 'app/packages/cookie-utils';
import CONST from 'app/common/lib/Const';
import context from 'app/common/lib/context';
import contextEvent from 'app/common/lib/ContextEvent';
import contextLogger from 'app/common/lib/ContextLogger';
import errorUtil from 'app/common/lib/errorUtil';
import ga from 'app/common/lib/ga';
import getInitModuleName from 'app/common/lib/util/getInitModuleName';
import isEmpty from 'lodash/isEmpty';
import Logging from 'app/common/lib/Logging';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import storage from 'app/common/lib/Storage';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import urlUtil from 'app/common/lib/urlUtil';
import user from 'app/common/lib/User';
import { getUserUnauthBehaviorDataMap } from 'app/common/lib/util/userBehaviorDataUtils';

const facebookLoader = () =>
  import(/* webpackChunkName: "FacebookCommon" */ 'app/common/lib/Facebook');
const socialConnectLoader = () =>
  import(/* webpackChunkName: "SocialConnectCommon" */ 'app/common/lib/SocialConnect');

const userRegistrationLog = Logging.getLogger('UserRegistration');

let _signupRequestAttempts = 0;

function track(action) {
  trackRegisterAction(`unauth_web_debug.UserRegistration.${action}`);
}

const userRegistration = {
  /**
   * Fast track a user's social registration
   *
   * @param {string} network The network for registration
   * @param {Object=} extraOptions
   * @return {!Promise}
   */
  fastTrackSocialRegistration(network, extraOptions) {
    track('fastTrackSocialRegistration_called');
    return socialConnectLoader().then(({ default: SocialConnect }) => {
      if (network === SocialConnect.FACEBOOK) {
        return userRegistration.fetchNetworkData(network).then((socialConnectData) => {
          if (!socialConnectData.email) {
            // We need an email to sign up a user
            return Promise.reject(new errorUtil.NoEmailProvidedError());
          }

          return userRegistration.sendImmediateFbRegisterRequest(socialConnectData, extraOptions);
        });
      } else {
        return Promise.resolve(undefined);
      }
    });
  },

  /**
   * Build the registration request.
   *
   * @param {Object.<string,*>} socialConnectData The social connect data returned by the fetchNetworkData call
   * @param {Object=} extraOptions Extra resource options to be passed along.
   * @return {!Promise} resolves with the resource's data
   */
  sendImmediateFbRegisterRequest(socialConnectData, extraOptions) {
    track('sendImmediateFbRegisterRequest_called');
    const resourceOptions = {
      first_name: socialConnectData.first_name,
      last_name: socialConnectData.last_name,
      email: socialConnectData.email,
      gender: socialConnectData.gender,
      image_url: socialConnectData.image_url,
      facebook_id: socialConnectData.id,
      facebook_token: socialConnectData.access_token,
      country: null, // Will be filled in server-sided since it's from the context
    };

    if (extraOptions) {
      Object.assign(resourceOptions, extraOptions);
    }

    return socialConnectLoader().then(({ default: SocialConnect }) => {
      userRegistration.addExtraSocialParams(SocialConnect.FACEBOOK, resourceOptions);
      return userRegistration.register(SocialConnect.FACEBOOK, resourceOptions);
    });
  },

  /**
   * Add any extra social parameters that should be sent along with the registration request.
   *
   * @param {string} network The network associated with the registration
   * @param {!Object} resourceOptions The registration options
   *
   * TODO(chris): This function having side-effects on an argument is sloppy.
   * Clean this up.
   */
  addExtraSocialParams(network, resourceOptions) {
    track('addExtraSocialParams_called');
    const localFacebookId = storage.sessionStorage.getItem(PCONST.SESSION_FACEBOOK_ID);
    const localFacebookToken = storage.sessionStorage.getItem(PCONST.SESSION_FACEBOOK_TOKEN);

    if (localFacebookId) {
      if (!resourceOptions.facebook_id) {
        resourceOptions.facebook_id = localFacebookId;
      }

      storage.sessionStorage.removeItem(PCONST.SESSION_FACEBOOK_ID);
    }

    if (localFacebookToken) {
      if (!resourceOptions.facebook_token) {
        resourceOptions.facebook_token = localFacebookToken;
      }

      storage.sessionStorage.removeItem(PCONST.SESSION_FACEBOOK_TOKEN);
    }

    // Check for auto follow data
    const followData = storage.sessionStorage.getItem(PCONST.SESSION_KEY_INVITE_FOLLOW);
    if (followData) {
      if (followData.invite_follow_board) {
        resourceOptions.board_to_follow = followData.invite_follow_board;
      }
      if (followData.invite_follow_user) {
        resourceOptions.user_to_follow = followData.invite_follow_user;
      }
    }
  },

  /**
   * Connect to a social network and populate the form.
   *
   * If the `network` argument is not facebook, we don't do anything.
   *
   * @param {string} network The name of the network (e.g. 'facebook')
   * @return {?Promise.<!Object>} resolves with network data for the user.
   */
  fetchNetworkData(network) {
    track('fetchNetworkData_called');
    return socialConnectLoader()
      .then(({ default: SocialConnect }) => {
        if (network !== SocialConnect.FACEBOOK) {
          return Promise.reject(new Error('Unsupported social network: ' + network));
        }

        trackRegisterAction('register_facebook');
        return facebookLoader();
      })
      .then(
        (Facebook) =>
          new Promise((resolve, reject) => {
            Facebook.ensureInit((_fb) => {
              _fb.getLoginStatus((response) => {
                if (!response.authResponse) {
                  return reject(new Error('not authed'));
                }

                // Need fields other than the default, so list all fields wanted to make the request string.
                const fieldsWanted = [
                  'picture.type(large)',
                  'id',
                  'first_name',
                  'last_name',
                  'email',
                  'gender',
                ];

                const requestString = '/me?fields=' + fieldsWanted.join(',');
                _fb.api(requestString, (response) => {
                  if (!response || response.error) {
                    return reject(new Error('FB.api errored: ' + response.error));
                  }

                  // The condition below should always be true, though Facebook SDK does not state
                  // this explicitly.
                  if (
                    response.picture &&
                    response.picture.data &&
                    response.picture.data.url &&
                    !response.picture.data.is_silhouette
                  ) {
                    response.image_url = response.picture.data.url;
                  }
                  return resolve(response);
                });
                return undefined;
              }, true); // means that it is forcing a network call to Facebook, TODO: possibly optimize this.
              // we don't always need to force a network call, but for some cases we do,
              // so generalizing for now. — Connor (June 6, 2013)
            }, user);
          }),
      );
  },

  /**
   * Registers the user using the resource.
   *
   * @param {string} network The network associated with the registration
   * @param {Object} resourceOptions The options for the register resource
   * @param {Object} options The options for the request
   * @param {bool} options.retryOnError Retry the request when an occurs (default true)
   * @return {!Promise} returns a deferred that resolves with the resource's data
   */
  register(network, resourceOptions, options) {
    track('register_called');
    options = Object.assign({ retryOnError: true }, options);
    const visitedPages = storage.localStorage.getItem(PCONST.LOCALSTORAGE_VISITED_PAGES);
    if (visitedPages) {
      resourceOptions.visited_pages = JSON.stringify(visitedPages);
    }
    resourceOptions.user_behavior_data = getUserUnauthBehaviorDataMap();

    /** @type {IResource} */
    const resource = userRegistration.getResource(resourceOptions);
    return resource.callCreate().then(
      (resourceResponse) => {
        ga.trackEvent('user_signup', PCONST.GA_ACTION_SUCCESS);
        storage.sessionStorage.removeItem(PCONST.SESSION_KEY_INVITE_FOLLOW);
        userRegistrationLog.debug('Login, successful', resourceResponse);
        Cookie.set(PCONST.COOKIE_USER_JUST_REGISTERED, '1');
        userRegistration.trackRegister(resourceOptions);
      },
      (error) => {
        ga.trackEvent('user_signup', PCONST.GA_ACTION_ERROR);
        if (error.api_error_code === CONST.API_ERROR_UNDERAGE_REGISTER_ATTEMPT) {
          // In case of an underage attempt, set the appropriate cookies to block further
          // signups, but let the caller to inspect and redirect as appropriate.
          // Cookie expiration set to 10 years.
          trackRegisterAction('multi_step_set_age_restrict_cookie');
          Cookie.set(PCONST.COOKIE_REGISTER_RESTRICT, '1', 24 * 365 * 10);
          Cookie.set(PCONST.COOKIE_PERSONALIZE_FROM_OFFSITE_BROWSING_COOKIE_NAME, 'disabled');
        }

        const isRetryableError =
          isEmpty(error.api_error_code) ||
          [CONST.API_SERVER_ERROR, CONST.API_REQUEST_TIMEOUT].includes(error.api_error_code);
        if (options.retryOnError && isRetryableError && _signupRequestAttempts < 3) {
          _signupRequestAttempts += 1;
          return userRegistration.register(network, resourceOptions);
        }

        throw error;
      },
    );
  },

  trackRegister(resourceOptions) {
    track('trackRegister_called');
    let _contextEvent;
    switch (resourceOptions.container) {
      case 'home_page':
        _contextEvent = contextEvent.fromEventType(PCONST.thrift.EventType.HOME_PAGE_SIGNUP);
        trackRegisterAction('signup_home_page');
        break;
      case 'category_pivot':
        trackRegisterAction('signup_category_pivot');
        break;
      case 'plain_signup_modal':
        _contextEvent = contextEvent.fromEventType(PCONST.thrift.EventType.PLAIN_MODAL_SIGNUP);
        trackRegisterAction('signup_plain_modal');
        break;
      case 'closeup_signup_modal':
        _contextEvent = contextEvent.fromEventType(PCONST.thrift.EventType.CLOSEUP_MODAL_SIGNUP);
        trackRegisterAction('signup_closeup_modal');
        break;
      case 'widescreen_signup_modal':
        _contextEvent = contextEvent.fromEventType(PCONST.thrift.EventType.WIDESCREEN_MODAL_SIGNUP);
        trackRegisterAction('signup_widescreen_modal');
        break;
      case 'pinit_button_landing':
        _contextEvent = contextEvent.fromEventType(
          PCONST.thrift.EventType.PIN_IT_BUTTON_LANDING_SIGNUP,
        );
        trackRegisterAction('signup_pinit_button_landing');
        break;
      case 'inspired_banner':
        trackRegisterAction('signup_inspired_banner');
        break;
      case 'send_pin_banner':
        _contextEvent = contextEvent.fromEventType(PCONST.thrift.EventType.SENT_PIN_LANDING_SIGNUP);
        trackRegisterAction('send_pin_banner');
        break;
      default:
        _contextEvent = contextEvent.fromEventType(
          PCONST.thrift.EventType.UNKNOWN_PLACEMENT_SIGNUP,
        );
        trackRegisterAction('signup_unknown_placement');
    }
    if (_contextEvent) {
      contextLogger.getInstance().addEvent(_contextEvent);
    }
    const referrer_url = document.referrer;
    let referrer;
    if (!referrer_url) {
      referrer = 'direct';
    } else if (referrer_url.indexOf('/t.co/') > -1) {
      referrer = 'twitter';
    } else if (referrer_url.indexOf('google.') > -1) {
      referrer = 'google';
    } else if (referrer_url.indexOf('bing.') > -1) {
      referrer = 'bing';
    } else if (referrer_url.indexOf('facebook.') > -1) {
      referrer = 'facebook';
    } else {
      referrer = 'other';
    }
    trackRegisterAction('signup_referrer.' + referrer);
    const referrer_module = urlUtil.getQueryStringParams().referrer || getInitModuleName();
    trackRegisterAction('signup_referrer_module.' + referrer_module);
    const userAgentPlatform = context.instance.user_agent_platform;
    if (userAgentPlatform === PCONST.USER_AGENT_PLATFORM_IOS) {
      trackRegisterAction('signup_mweb.iphone_web');
    } else if (userAgentPlatform === PCONST.USER_AGENT_PLATFORM_IPAD) {
      trackRegisterAction('signup_mweb.ipad_web');
    } else if (userAgentPlatform === PCONST.USER_AGENT_PLATFORM_ANDROID) {
      trackRegisterAction('signup_mweb.android_web');
    }

    if (Cookie.get(PCONST.COOKIE_LAST_VISITED_PIN_PAGES)) {
      // If last visited pin pages use for personalization still exist, clear cookie
      Cookie.unset(PCONST.COOKIE_LAST_VISITED_PIN_PAGES);
    }
  },

  /**
   * @private
   */
  getResource(resourceOptions) {
    track('getResource_called');
    return ResourceFactory.create('UserRegisterResource', resourceOptions);
  },
};

export default userRegistration;
